import { IconId } from '@tabler/icons-react';
import { Checkbox } from '@zorro/design-system/v2/Checkbox';
import { RadioCard, RadioGroup } from '@zorro/design-system/v2/Radio';
import { TextInput } from '@zorro/design-system/v2/TextInput';
import {
  FormErrorMessage,
  Grid,
  Icon,
  Stack,
  Text,
} from '@zorro/web/design-system';
import {
  EnrollmentConfirmationFormFields,
  PlanMarketConfig,
  planMarketEnrollmentValues,
  useAnalytics,
} from '@zorro/web/shared-ui';
import { screamingSnakeCaseToText } from '@zorro/web/ui-utils';
import { Controller, UseFormReturn } from 'react-hook-form';

type Props = {
  sectionNumber: number;
  onboardingPeriodId: string;
  enrollmentConfirmationForm: UseFormReturn<EnrollmentConfirmationFormFields>;
};

export const EnrollmentPolicyDetailsSection = ({
  sectionNumber,
  onboardingPeriodId,
  enrollmentConfirmationForm,
}: Props) => {
  const { sendEvent } = useAnalytics();
  const {
    control,
    formState: { errors },
    watch,
    setValue,
    clearErrors,
    trigger,
  } = enrollmentConfirmationForm;

  const memberIdField = watch('memberId');
  const noMemberAvailable = watch('noMemberAvailable');

  return (
    <Stack my="xl" ml="xs" gap="xxs">
      <Text w="100%" size="md" fw={600}>
        {sectionNumber}. Policy details
      </Text>
      <Stack ml="md" gap="md">
        <Text size="sm" c="var(--color-text-default-secondary)">
          Verify enrollment type
        </Text>
        <Stack gap="xxs">
          <Controller
            name="planMarket"
            control={control}
            render={({ field: { value, onChange, ...rest } }) => (
              <RadioGroup
                {...rest}
                value={String(value)}
                required
                onChange={async (newValue) => {
                  onChange(newValue);
                  await sendEvent('select_enrollment_type', {
                    onboarding_period_id: onboardingPeriodId,
                    enrollment_type: newValue.toLowerCase(),
                  });
                }}
              >
                <Grid gap="md">
                  {planMarketEnrollmentValues.map((radioValue) => {
                    const { color, icon: IconComponent } =
                      PlanMarketConfig[radioValue];
                    return (
                      <Grid.Col span={4} key={radioValue}>
                        <RadioCard
                          size="md"
                          icon={<IconComponent />}
                          iconColor={color}
                          value={radioValue}
                          checked={radioValue === value}
                          label={screamingSnakeCaseToText(radioValue)}
                        />
                      </Grid.Col>
                    );
                  })}
                </Grid>
              </RadioGroup>
            )}
          />
          <FormErrorMessage fieldName="planMarket" errors={errors} />
        </Stack>

        <Grid gap="xs">
          <Grid.Col span={8}>
            <Controller
              control={control}
              name="memberId"
              render={({ field }) => (
                <TextInput
                  {...field}
                  size="md"
                  label="Member ID"
                  placeholder="Type Member ID"
                  leftSection={<Icon icon={IconId} />}
                  disabled={noMemberAvailable}
                  required
                />
              )}
            />
            <FormErrorMessage errors={errors} fieldName="memberId" />
          </Grid.Col>
          <Grid.Col span={12}>
            <Controller
              control={control}
              name="noMemberAvailable"
              render={({ field: { value, ...rest } }) => (
                <Checkbox
                  {...rest}
                  checked={Boolean(value)}
                  label="No Member ID available"
                  disabled={Boolean(memberIdField)}
                  onChange={async (event) => {
                    const checked = event.currentTarget.checked;
                    rest.onChange(checked);
                    if (checked) {
                      setValue('memberId', '');
                      clearErrors('memberId');
                    } else {
                      await trigger('memberId');
                    }
                  }}
                />
              )}
            />
          </Grid.Col>
        </Grid>
      </Stack>
    </Stack>
  );
};
